import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { Search, FolderOpen, Tag, FileText, Calendar, Check, Mail, Phone, CreditCard, Receipt } from "lucide-react";
import { toast } from "sonner";
import { SiteHeader } from "@/components/layout/SiteHeader";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { BASE } from "@/lib/api";
import { useLang } from "@/i18n/LanguageProvider";
import type { TKey } from "@/i18n/translations";

export const Route = createFileRoute("/track")({
  head: () => ({ meta: [{ title: "Track Order | BAZZAR 3D" }] }),
  component: TrackPage,
});

type StatusKey = "pending" | "confirmed" | "in_progress" | "completed";

type OrderItem = {
  title: string;
  planName?: string | null;
  qty: number;
  price: number;
  originalPrice?: number | null;
};

type TimelineEvent = {
  status?: string;
  label?: string;
  note?: string;
  at?: string;
  created_at?: string;
};

type Order = {
  number: string;
  status: string;
  paymentStatus?: string;
  paymentMethod?: string;
  total?: number;
  subtotal?: number;
  vat?: number;
  couponDiscount?: number;
  currency?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
};

type Result = {
  order: Order;
  items: OrderItem[];
  timeline: TimelineEvent[];
};

const STAGE_KEYS: StatusKey[] = ["pending", "confirmed", "in_progress", "completed"];

function mapStatus(s: string): StatusKey {
  const v = (s || "").toLowerCase();
  if (["pending", "new", "awaiting"].includes(v)) return "pending";
  if (["confirmed", "paid"].includes(v)) return "confirmed";
  if (["in_progress", "processing", "designing", "reviewing"].includes(v)) return "in_progress";
  if (["completed", "delivered", "done"].includes(v)) return "completed";
  return "pending";
}

type Method = "email" | "phone";

function TrackPage() {
  const { t, lang, dir } = useLang();
  const [method, setMethod] = useState<Method>("phone");
  const [contact, setContact] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [notFound, setNotFound] = useState(false);

  const paymentStatusLabel = (s?: string) => {
    const v = (s || "").toLowerCase();
    if (v === "paid") return t("track.payStatus.paid");
    if (v === "unpaid") return t("track.payStatus.unpaid");
    if (v === "refunded") return t("track.payStatus.refunded");
    return s || "—";
  };
  const paymentMethodLabel = (s?: string) => {
    const v = (s || "").toLowerCase();
    if (v === "cod") return t("track.payMethod.cod");
    if (v === "card") return t("track.payMethod.card");
    if (v === "bank") return t("track.payMethod.bank");
    return s || "—";
  };

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!contact.trim() || !orderNumber.trim()) {
      toast.error(method === "email" ? t("track.err.fillEmail") : t("track.err.fillPhone"));
      return;
    }
    setLoading(true);
    setNotFound(false);
    setResult(null);
    try {
      const params = new URLSearchParams({ number: orderNumber.trim() });
      params.set(method, contact.trim());
      const res = await fetch(`${BASE}/lookup-order?${params.toString()}`, {
        headers: { Accept: "application/json" },
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json || json.success === false) {
        if (res.status === 404) setNotFound(true);
        else toast.error(json?.message || t("track.err.lookup"));
        return;
      }
      const payload = json.data ?? json;
      const order: Order | undefined = payload?.order;
      if (!order) {
        setNotFound(true);
        return;
      }
      setResult({
        order,
        items: Array.isArray(payload?.items) ? payload.items : [],
        timeline: Array.isArray(payload?.timeline) ? payload.timeline : [],
      });
    } catch (e: any) {
      toast.error(e?.message || t("track.err.generic"));
    } finally {
      setLoading(false);
    }
  }

  const currentIndex = result ? STAGE_KEYS.findIndex((s) => s === mapStatus(result.order.status)) : -1;
  const currency = result?.order.currency || "SAR";
  const locale = lang === "ar" ? "ar-SA" : "en-US";
  const textEnd = dir === "rtl" ? "text-end" : "text-start";

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteHeader />
      <main className="flex-1">
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-10">
          <div className="text-center">
            <span className="text-xs font-bold uppercase tracking-widest text-primary">{t("track.kicker")}</span>
            <h1 className="mt-3 text-4xl font-extrabold text-foreground sm:text-5xl">{t("track.title")}</h1>
            <p className="mx-auto mt-4 max-w-xl text-sm text-muted-foreground">
              {t("track.subtitle")}
            </p>
          </div>

          {/* Method tabs */}
          <div className="mx-auto mt-8 flex max-w-md items-center justify-center gap-2 rounded-full border border-border bg-white p-1">
            <button
              type="button"
              onClick={() => { setMethod("phone"); setContact(""); }}
              className={["inline-flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition",
                method === "phone" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"].join(" ")}
            >
              <Phone className="h-4 w-4" /> {t("track.tab.phone")}
            </button>
            <button
              type="button"
              onClick={() => { setMethod("email"); setContact(""); }}
              className={["inline-flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition",
                method === "email" ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"].join(" ")}
            >
              <Mail className="h-4 w-4" /> {t("track.tab.email")}
            </button>
          </div>

          {/* Form */}
          <form
            onSubmit={onSubmit}
            className="mx-auto mt-6 flex max-w-5xl flex-col items-stretch gap-3 rounded-3xl border border-border bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:gap-2 sm:p-3"
          >
            <div className="relative flex-1">
              <span className="pointer-events-none absolute inset-y-0 end-4 flex items-center text-muted-foreground">
                {method === "email" ? <Mail className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
              </span>
              <input
                type={method === "email" ? "email" : "tel"}
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder={method === "email" ? t("track.placeholder.email") : t("track.placeholder.phone")}
                className={`h-12 w-full rounded-full border border-border bg-background ps-5 pe-11 text-sm text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 ${textEnd}`}
              />
            </div>
            <div className="relative flex-1">
              <span className="pointer-events-none absolute inset-y-0 end-4 flex items-center text-muted-foreground">
                <FileText className="h-4 w-4" />
              </span>
              <input
                type="text"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder={t("track.placeholder.order")}
                className={`h-12 w-full rounded-full border border-border bg-background ps-5 pe-11 text-sm text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 ${textEnd}`}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-primary px-6 text-sm font-bold text-primary-foreground shadow-[0_10px_24px_-10px_rgba(0,174,198,0.6)] transition hover:bg-primary-dark disabled:opacity-60"
            >
              <Search className="h-4 w-4" />
              {loading ? t("track.submitting") : t("track.submit")}
            </button>
          </form>

          {notFound && (
            <p className="mx-auto mt-6 max-w-3xl rounded-2xl border border-border bg-muted/40 p-4 text-center text-sm text-muted-foreground">
              {method === "email" ? t("track.notFound.email") : t("track.notFound.phone")}
            </p>
          )}

          {result && (
            <div className="mx-auto mt-10 max-w-5xl space-y-6">
              {/* Header card */}
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-border bg-white p-6 sm:p-8">
                <div className={textEnd}>
                  <div className="text-xs font-medium text-muted-foreground">{t("track.orderNumber")}</div>
                  <div className="mt-1 text-lg font-extrabold text-foreground" dir="ltr">{result.order.number}</div>
                </div>
                <div className={textEnd}>
                  <div className="text-xs font-medium text-muted-foreground">{t("track.createdAt")}</div>
                  <div className="mt-1 text-sm font-bold text-foreground">
                    {result.order.createdAt
                      ? new Date(result.order.createdAt.replace(" ", "T")).toLocaleString(locale)
                      : "—"}
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div className="rounded-3xl border border-border bg-white p-6 sm:p-8">
                <h2 className={`mb-8 ${textEnd} text-xl font-extrabold text-foreground`}>{t("track.statusTitle")}</h2>
                <Timeline currentIndex={currentIndex} dir={dir} t={t} />
              </div>

              {/* Info cards */}
              <div className="grid gap-4 sm:grid-cols-2">
                <InfoCard icon={<CreditCard className="h-5 w-5" />} label={t("track.payStatus")} value={paymentStatusLabel(result.order.paymentStatus)} textEnd={textEnd} />
                <InfoCard icon={<Receipt className="h-5 w-5" />} label={t("track.payMethod")} value={paymentMethodLabel(result.order.paymentMethod)} textEnd={textEnd} />
                <InfoCard icon={<Tag className="h-5 w-5" />} label={t("track.total")} value={`${result.order.total ?? 0} ${currency}`} textEnd={textEnd} />
                <InfoCard icon={<Calendar className="h-5 w-5" />} label={t("track.lastUpdate")} value={result.order.updatedAt ? new Date(result.order.updatedAt.replace(" ", "T")).toLocaleString(locale) : "—"} textEnd={textEnd} />
              </div>

              {/* Items */}
              {result.items.length > 0 && (
                <div className="rounded-3xl border border-border bg-white p-6 sm:p-8">
                  <h2 className={`mb-6 ${textEnd} text-xl font-extrabold text-foreground`}>{t("track.itemsTitle")}</h2>
                  <div className="space-y-3">
                    {result.items.map((it, i) => (
                      <div key={i} className="flex items-center justify-between gap-4 rounded-2xl border border-border bg-background p-4">
                        <div className="flex items-center gap-2 text-sm font-bold text-foreground">
                          <span className="text-muted-foreground">×{it.qty}</span>
                          <span>{it.price} {currency}</span>
                        </div>
                        <div className={`flex-1 ${textEnd}`}>
                          <div className="text-sm font-bold text-foreground">{it.title}</div>
                          {it.planName && <div className="mt-0.5 text-xs text-muted-foreground">{it.planName}</div>}
                        </div>
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-light text-primary">
                          <FolderOpen className="h-4 w-4" />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Totals */}
                  <div className="mt-6 space-y-2 rounded-2xl bg-muted/40 p-4 text-sm">
                    <Row label={t("track.subtotal")} value={`${result.order.subtotal ?? 0} ${currency}`} />
                    {!!result.order.couponDiscount && (
                      <Row label={t("track.couponDiscount")} value={`- ${result.order.couponDiscount} ${currency}`} />
                    )}
                    <Row label={t("track.vat")} value={`${result.order.vat ?? 0} ${currency}`} />
                    <div className="my-2 h-px bg-border" />
                    <Row label={t("track.total")} value={`${result.order.total ?? 0} ${currency}`} bold />
                  </div>
                </div>
              )}

              {/* Timeline events (if any) */}
              {result.timeline.length > 0 && (
                <div className="rounded-3xl border border-border bg-white p-6 sm:p-8">
                  <h2 className={`mb-6 ${textEnd} text-xl font-extrabold text-foreground`}>{t("track.logTitle")}</h2>
                  <ul className="space-y-3">
                    {result.timeline.map((ev, i) => (
                      <li key={i} className="flex items-start justify-between gap-3 rounded-2xl border border-border bg-background p-4">
                        <div className="text-xs text-muted-foreground">
                          {ev.at || ev.created_at ? new Date((ev.at || ev.created_at)!.replace(" ", "T")).toLocaleString(locale) : ""}
                        </div>
                        <div className={`flex-1 ${textEnd}`}>
                          <div className="text-sm font-bold text-foreground">{ev.label || ev.status || t("track.update")}</div>
                          {ev.note && <div className="mt-1 text-xs text-muted-foreground">{ev.note}</div>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className={bold ? "text-base font-extrabold text-foreground" : "text-muted-foreground"}>{value}</span>
      <span className={bold ? "text-base font-extrabold text-foreground" : "text-foreground"}>{label}</span>
    </div>
  );
}

function Timeline({ currentIndex, dir, t }: { currentIndex: number; dir: "rtl" | "ltr"; t: (k: TKey) => string }) {
  const ordered = dir === "rtl" ? [...STAGE_KEYS].reverse() : [...STAGE_KEYS];
  return (
    <div className="flex items-start justify-between gap-2" dir={dir}>
      {ordered.map((stage, i) => {
        const logicalIndex = STAGE_KEYS.findIndex((s) => s === stage);
        const reached = currentIndex >= 0 && logicalIndex <= currentIndex;
        const isCurrent = logicalIndex === currentIndex;
        const isNotLast = i < ordered.length - 1;
        const nextLogical = STAGE_KEYS.findIndex((s) => s === ordered[i + 1]);
        const lineActive = currentIndex >= 0 && nextLogical >= 0 && nextLogical <= currentIndex;
        return (
          <div key={stage} className="flex flex-1 flex-col items-center">
            <div className="flex w-full items-center">
              <div className="flex flex-1 justify-center">
                <div
                  className={[
                    "flex h-12 w-12 items-center justify-center rounded-full text-sm font-bold transition",
                    reached ? "bg-primary text-primary-foreground shadow-[0_8px_20px_-8px_rgba(0,174,198,0.6)]" : "bg-muted text-muted-foreground",
                    isCurrent ? "ring-2 ring-primary ring-offset-2 ring-offset-white" : "",
                  ].join(" ")}
                >
                  {reached && !isCurrent ? <Check className="h-5 w-5" strokeWidth={3} /> : logicalIndex + 1}
                </div>
              </div>
              {isNotLast && (
                <div className={["h-[2px] flex-1 rounded-full", lineActive ? "bg-primary" : "bg-border"].join(" ")} />
              )}
            </div>
            <div className={["mt-3 text-center text-xs font-semibold sm:text-sm", reached ? "text-foreground" : "text-muted-foreground"].join(" ")}>
              {t(`track.stage.${stage}` as TKey)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function InfoCard({ icon, label, value, textEnd }: { icon: React.ReactNode; label: string; value: string; textEnd: string }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl border border-border bg-white p-5">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-light text-primary">
        {icon}
      </div>
      <div className={`flex-1 ${textEnd}`}>
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
        <div className="mt-1 text-base font-bold text-foreground">{value}</div>
      </div>
    </div>
  );
}
